import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { toast } from "sonner";
import { LogIn, Mail, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Login por link magico do Portal do Cliente.
 * - Se a URL trouxer ?token=..., consome o token e faz login automatico.
 * - Caso contrario, mostra o formulario para pedir o link por email.
 */
export default function PortalLogin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [phase, setPhase] = useState(token ? "consuming" : "request"); // request | sent | consuming | error
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.post("/portal-auth/magic-link/consume", { token })
      .then(({ data }) => {
        localStorage.setItem("access_token", data.access_token);
        if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
        toast.success("Autenticado com sucesso.");
        nav("/portal", { replace: true });
      })
      .catch((e) => {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Nao foi possivel autenticar.");
        setPhase("error");
      });
  }, [token, nav]);

  const requestLink = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Indique o seu email.");
    setSending(true);
    try {
      await api.post("/portal-auth/magic-link/request", { email });
      setPhase("sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao pedir link.");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-6" data-testid="portal-login-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-tint p-8">
        <div className="flex items-center gap-2 mb-1">
          <LogIn className="text-blue" size={20} />
          <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold">Portal do Cliente</div>
        </div>
        <h1 className="text-navy text-2xl font-bold tracking-tight mb-6">Entrar sem password</h1>

        {phase === "consuming" && (
          <div className="text-center py-8 text-muted2">
            <div className="animate-spin w-8 h-8 border-2 border-blue border-t-transparent rounded-full mx-auto mb-3" />
            A validar o link...
          </div>
        )}

        {phase === "error" && (
          <div className="border border-red-200 bg-red-50 text-red-700 rounded-lg p-4 mb-4 flex items-start gap-2" data-testid="portal-login-error">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div className="text-sm">{error}</div>
          </div>
        )}

        {(phase === "request" || phase === "error") && (
          <form onSubmit={requestLink} className="space-y-4">
            <p className="text-sm text-muted2 leading-relaxed">
              Vamos enviar-lhe um link seguro para o seu email. E valido durante 15 minutos e so pode ser usado uma vez.
            </p>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted2 font-semibold mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                autoFocus data-testid="portal-login-email"
                placeholder="voce@empresa.pt"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            </div>
            <button type="submit" disabled={sending} data-testid="portal-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue text-white font-semibold hover:bg-navy disabled:opacity-60">
              <Mail size={16} /> {sending ? "A enviar..." : "Enviar link magico"}
            </button>
            <div className="text-center text-xs text-muted2 pt-2">
              Ainda tem password? <a href="/login" className="text-blue font-semibold hover:underline">Entrar com password</a>
            </div>
          </form>
        )}

        {phase === "sent" && (
          <div className="text-center py-6" data-testid="portal-login-sent">
            <CheckCircle2 className="text-emerald-500 mx-auto mb-3" size={44} />
            <div className="text-navy font-bold mb-1">Verifique o seu email</div>
            <p className="text-sm text-muted2 leading-relaxed">
              Se o email <b>{email}</b> estiver associado a uma conta, recebera o link em instantes. O link e valido por 15 minutos.
            </p>
            <button onClick={() => { setPhase("request"); setEmail(""); }} data-testid="portal-login-retry"
              className="mt-4 text-sm text-blue font-semibold hover:underline">
              Pedir outro link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
